import React, { useState, useRef, useCallback } from 'react'
import { Button } from './Button'
import { Card, CardContent } from './Card'
import { Trash2, Plus, Play, Zap, Mail, Database, FileText, Clock } from 'lucide-react'

interface WorkflowBlock {
  id: string
  type: 'trigger' | 'action' | 'condition'
  category: string
  title: string
  description: string
  icon: React.ReactNode
  x: number
  y: number
  connected?: string[]
}

interface Connection {
  id: string
  from: string
  to: string
}

interface DragDropCanvasProps {
  onWorkflowChange?: (blocks: WorkflowBlock[], connections: Connection[]) => void
  onSimulate?: () => void
}

const blockTypes = {
  triggers: [
    {
      id: 'email-received',
      type: 'trigger' as const,
      category: 'Communication',
      title: 'Email Received',
      description: 'Triggers when a new email arrives',
      icon: <Mail className="w-4 h-4" />
    },
    {
      id: 'schedule',
      type: 'trigger' as const,
      category: 'Time',
      title: 'Schedule',
      description: 'Triggers at specific times or intervals',
      icon: <Clock className="w-4 h-4" />
    },
    {
      id: 'form-submit',
      type: 'trigger' as const,
      category: 'Web',
      title: 'Form Submitted',
      description: 'Triggers when a web form is submitted',
      icon: <FileText className="w-4 h-4" />
    }
  ],
  actions: [
    {
      id: 'send-email',
      type: 'action' as const,
      category: 'Communication',
      title: 'Send Email',
      description: 'Send an automated email response',
      icon: <Mail className="w-4 h-4" />
    },
    {
      id: 'save-data',
      type: 'action' as const,
      category: 'Data',
      title: 'Save to Database',
      description: 'Store information in your database',
      icon: <Database className="w-4 h-4" />
    },
    {
      id: 'create-task',
      type: 'action' as const,
      category: 'Productivity',
      title: 'Create Task',
      description: 'Generate a new task or reminder',
      icon: <Plus className="w-4 h-4" />
    }
  ]
}

export function DragDropCanvas({ onWorkflowChange, onSimulate }: DragDropCanvasProps) {
  const [blocks, setBlocks] = useState<WorkflowBlock[]>([])
  const [connections, setConnections] = useState<Connection[]>([])
  const [draggedItem, setDraggedItem] = useState<any>(null)
  const [isConnecting, setIsConnecting] = useState(false)
  const [connectFrom, setConnectFrom] = useState<string | null>(null)
  const canvasRef = useRef<HTMLDivElement>(null)

  const handleDragStart = (e: React.DragEvent, item: any) => {
    setDraggedItem(item)
    e.dataTransfer.effectAllowed = 'copy'
  }

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault()
    e.dataTransfer.dropEffect = 'copy'
  }

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault()
    if (!draggedItem || !canvasRef.current) return

    const rect = canvasRef.current.getBoundingClientRect()
    const x = e.clientX - rect.left
    const y = e.clientY - rect.top

    const newBlock: WorkflowBlock = {
      ...draggedItem,
      id: `${draggedItem.id}-${Date.now()}`,
      x: Math.max(0, x - 60),
      y: Math.max(0, y - 30),
      connected: []
    }

    const updatedBlocks = [...blocks, newBlock]
    setBlocks(updatedBlocks)
    setDraggedItem(null)
    
    onWorkflowChange?.(updatedBlocks, connections)
  }

  const handleBlockClick = (blockId: string) => {
    if (isConnecting && connectFrom && connectFrom !== blockId) {
      // Create connection
      const newConnection: Connection = {
        id: `${connectFrom}-${blockId}`,
        from: connectFrom,
        to: blockId
      }
      
      const updatedConnections = [...connections, newConnection]
      setConnections(updatedConnections)
      setIsConnecting(false)
      setConnectFrom(null)
      
      onWorkflowChange?.(blocks, updatedConnections)
    } else if (isConnecting) {
      setIsConnecting(false)
      setConnectFrom(null)
    } else {
      setIsConnecting(true)
      setConnectFrom(blockId)
    }
  }

  const handleDeleteBlock = (blockId: string) => {
    const updatedBlocks = blocks.filter(b => b.id !== blockId)
    const updatedConnections = connections.filter(c => c.from !== blockId && c.to !== blockId)
    
    setBlocks(updatedBlocks)
    setConnections(updatedConnections)
    onWorkflowChange?.(updatedBlocks, updatedConnections)
  }

  const clearCanvas = () => {
    setBlocks([])
    setConnections([])
    setIsConnecting(false)
    setConnectFrom(null)
    onWorkflowChange?.([], [])
  }

  return (
    <div className="flex h-[600px] border rounded-lg overflow-hidden">
      {/* Block Palette */}
      <div className="w-64 bg-muted/30 border-r p-4 overflow-y-auto">
        <h3 className="font-semibold mb-4">Workflow Blocks</h3>
        
        <div className="space-y-4">
          <div>
            <h4 className="text-sm font-medium text-muted-foreground mb-2">Triggers</h4>
            <div className="space-y-2">
              {blockTypes.triggers.map((trigger) => (
                <div
                  key={trigger.id}
                  draggable
                  onDragStart={(e) => handleDragStart(e, trigger)}
                  className="p-2 bg-background border rounded cursor-move hover:shadow-sm transition-shadow"
                >
                  <div className="flex items-center gap-2 mb-1">
                    <div className="text-green-600">{trigger.icon}</div>
                    <span className="text-sm font-medium">{trigger.title}</span>
                  </div>
                  <p className="text-xs text-muted-foreground">{trigger.description}</p>
                </div>
              ))}
            </div>
          </div>

          <div>
            <h4 className="text-sm font-medium text-muted-foreground mb-2">Actions</h4>
            <div className="space-y-2">
              {blockTypes.actions.map((action) => (
                <div
                  key={action.id}
                  draggable
                  onDragStart={(e) => handleDragStart(e, action)}
                  className="p-2 bg-background border rounded cursor-move hover:shadow-sm transition-shadow"
                >
                  <div className="flex items-center gap-2 mb-1">
                    <div className="text-blue-600">{action.icon}</div>
                    <span className="text-sm font-medium">{action.title}</span>
                  </div>
                  <p className="text-xs text-muted-foreground">{action.description}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Canvas */}
      <div className="flex-1 flex flex-col">
        <div className="p-4 border-b bg-background">
          <div className="flex items-center justify-between">
            <h3 className="font-semibold">Workflow Canvas</h3>
            <div className="flex gap-2">
              <Button
                onClick={() => setIsConnecting(!isConnecting)}
                variant={isConnecting ? "default" : "outline"}
                size="sm"
              >
                <Zap className="w-4 h-4 mr-1" />
                {isConnecting ? 'Connecting...' : 'Connect'}
              </Button>
              <Button onClick={onSimulate} size="sm" className="gap-1">
                <Play className="w-4 h-4" />
                Simulate
              </Button>
              <Button onClick={clearCanvas} variant="outline" size="sm">
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
          </div>
          {isConnecting && (
            <p className="text-xs text-muted-foreground mt-2">
              Click on blocks to connect them. Click first block, then target block.
            </p>
          )}
        </div>

        <div
          ref={canvasRef}
          onDragOver={handleDragOver}
          onDrop={handleDrop}
          className="flex-1 relative bg-dots-pattern bg-muted/10 overflow-hidden"
        >
          {/* Connections */}
          <svg className="absolute inset-0 w-full h-full pointer-events-none">
            {connections.map((connection) => {
              const fromBlock = blocks.find(b => b.id === connection.from)
              const toBlock = blocks.find(b => b.id === connection.to)
              
              if (!fromBlock || !toBlock) return null
              
              return (
                <line
                  key={connection.id}
                  x1={fromBlock.x + 60}
                  y1={fromBlock.y + 30}
                  x2={toBlock.x + 60}
                  y2={toBlock.y + 30}
                  stroke="#3b82f6"
                  strokeWidth="2"
                  markerEnd="url(#arrowhead)"
                />
              )
            })}
            <defs>
              <marker
                id="arrowhead"
                markerWidth="10"
                markerHeight="7"
                refX="10"
                refY="3.5"
                orient="auto"
              >
                <polygon
                  points="0 0, 10 3.5, 0 7"
                  fill="#3b82f6"
                />
              </marker>
            </defs>
          </svg>

          {/* Blocks */}
          {blocks.map((block) => (
            <div
              key={block.id}
              style={{
                position: 'absolute',
                left: block.x,
                top: block.y,
              }}
              className={`w-32 bg-background border-2 rounded-lg p-2 cursor-pointer transition-all ${
                isConnecting && connectFrom === block.id
                  ? 'border-primary ring-2 ring-primary/20'
                  : isConnecting
                  ? 'border-muted-foreground/30 hover:border-primary/50'
                  : 'border-border hover:border-primary/50'
              }`}
              onClick={() => handleBlockClick(block.id)}
            >
              <div className="flex items-center justify-between mb-1">
                <div className={`${block.type === 'trigger' ? 'text-green-600' : 'text-blue-600'}`}>
                  {block.icon}
                </div>
                <button
                  onClick={(e) => {
                    e.stopPropagation()
                    handleDeleteBlock(block.id)
                  }}
                  className="text-red-500 hover:text-red-700 opacity-0 hover:opacity-100 transition-opacity"
                >
                  <Trash2 className="w-3 h-3" />
                </button>
              </div>
              <div className="text-xs font-medium mb-1">{block.title}</div>
              <div className="text-xs text-muted-foreground">{block.category}</div>
            </div>
          ))}

          {blocks.length === 0 && (
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="text-center text-muted-foreground">
                <Zap className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p className="text-lg font-medium mb-2">Build Your Workflow</p>
                <p className="text-sm">Drag blocks from the left panel to get started</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}